import { z } from "zod";

/**
 * US-E19.F1-0200-S01 (@UC9296/@UC9297) — CONTRACT luồng SỬA-NHẦM (correction) 🔴 money.
 *
 * "Sửa" bút toán KHÔNG BAO GIỜ là UPDATE/DELETE sổ cái (append-only B2/G2). Sửa =
 *   REVERSAL(bút toán gốc) + POST(bút toán ĐÚNG) — cả 2 trong 1 transaction.
 * Contract này mô tả yêu cầu sửa + kết quả (3 con trỏ: gốc · reversal · đúng).
 *
 * B1 (no-float): mọi tiền = **STRING** minor (BigInt serialize an toàn qua HTTP), regex CHỈ chữ số ⇒
 * `500.0` / number JS bị từ chối NGAY Ở BIÊN (không lọt vào service dưới dạng float).
 * `.strict()` mọi object ⇒ field lạ bị từ chối (không leak field không kiểm soát vào tầng tiền).
 */

/** Chỉ chữ số (minor units) — KHÔNG dấu chấm/phẩy/âm. */
const MONEY_INTEGER_STRING_PATTERN = /^\d+$/;

/** Trần PG signed BIGINT (chống overflow P2020 → 500). */
const PG_BIGINT_MAX = 9223372036854775807n;

/** Trần độ dài khoá nghiệp vụ (đồng bộ `deriveBusinessIdempotencyKey` — domain S02). */
export const BUSINESS_IDEMPOTENCY_KEY_MAX = 200;

/** Khoá idempotency NGHIỆP VỤ (ổn định theo hành động — KHÔNG random mỗi lần bấm). */
export const businessIdempotencyKeySchema = z
  .string()
  .trim()
  .min(1)
  .max(BUSINESS_IDEMPOTENCY_KEY_MAX);

/** Tiền minor STRING dương ≤ trần BIGINT (B1). */
const positiveMinorString = z
  .string()
  .trim()
  .regex(MONEY_INTEGER_STRING_PATTERN, "Số tiền phải là số nguyên (đơn vị nhỏ nhất)")
  .refine((v) => {
    try {
      const n = BigInt(v);
      return n > 0n && n <= PG_BIGINT_MAX;
    } catch {
      return false;
    }
  }, "Số tiền phải lớn hơn 0 và không vượt giới hạn");

/** Lý do sửa — BẮT BUỘC (dấu vết audit "ai sửa, vì sao"). Rỗng/toàn khoảng trắng ⇒ từ chối. */
export const CORRECTION_REASON_MAX = 500;

/** Giá trị ĐÚNG sau khi sửa: số tiền (bắt buộc) + người nhận đúng (khi sửa NHẦM NGƯỜI). */
export const correctedValueSchema = z
  .object({
    amountMinor: positiveMinorString,
    /** Sửa NHẦM NGƯỜI NHẬN: member ĐÚNG (thuộc nhóm sở hữu quỹ). Vắng ⇒ giữ người của bút toán gốc. */
    payeeMemberId: z.string().uuid().optional(),
  })
  .strict();

/** POST /v1/funds/:id/ledger-corrections — yêu cầu sửa 1 bút toán ghi nhầm. */
export const correctionRequestSchema = z
  .object({
    /** Bút toán NHẦM cần đảo (phải thuộc đúng quỹ — service chốt tenant, sai ⇒ 404). */
    targetTxId: z.string().uuid(),
    /** Khoá nghiệp vụ chống bấm kép: 2 lần cùng key ⇒ ĐÚNG 1 reversal + 1 post (không nhân đôi). */
    correctionKey: businessIdempotencyKeySchema,
    reason: z.string().trim().min(1).max(CORRECTION_REASON_MAX),
    corrected: correctedValueSchema,
  })
  .strict();

export type CorrectionRequest = z.infer<typeof correctionRequestSchema>;
export type CorrectedValue = z.infer<typeof correctedValueSchema>;

/** Kết quả sửa: 3 con trỏ bút toán (gốc BẤT BIẾN · reversal · đúng) + số ĐÚNG (string minor). */
export const correctionResponseSchema = z
  .object({
    targetTxId: z.string().uuid(),
    reversalTxId: z.string().uuid(),
    correctedTxId: z.string().uuid(),
    correctedAmountMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    currency: z.string().length(3),
    reason: z.string(),
    correctedAt: z.string().datetime(),
  })
  .strict();

export type CorrectionResponse = z.infer<typeof correctionResponseSchema>;
